import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Clock, LogIn, LogOut, MapPin, FileText, CheckCircle2, AlertCircle } from "lucide-react";
import DailyIconPickerDialog from "./DailyIconPickerDialog";
import { fmtDatePR, fmtTimePR } from "../lib/datetime";

const PUNCH_LOCATIONS = [
  "At Home", "In Office", "Client Visit", "In Academy",
  "In Training", "In Travel", "On Tour", "Work From Home", "Other",
];

const formatTime = (iso) => fmtTimePR(iso, "—");

export default function PunchWidget({ onChange, onSubmitReport, onViewReport }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [askLoc, setAskLoc] = useState(null);  // "in" | "out"
  const [chosenLoc, setChosenLoc] = useState("");
  const [otherText, setOtherText] = useState("");
  const [reportStatus, setReportStatus] = useState(null);
  const [askReport, setAskReport] = useState(false);
  const [askIcon, setAskIcon] = useState(false);
  const [tick, setTick] = useState(0); // triggers live re-render for the working-hours counter

  const load = async () => {
    try {
      const { data } = await api.get("/my-attendance/today");
      setToday(data);
    } catch (e) {
      console.error("PunchWidget: failed to load today's attendance", e);
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    try {
      const { data } = await api.get("/my-daily-report-status");
      setReportStatus(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadReport(); }, []);

  // v109.9.33 — live-ticking working-hours counter. Only ticks while the user
  // has punched IN but not yet punched OUT. Once punch-out lands, the counter
  // is frozen using the server-computed `hours_worked_hms` value.
  useEffect(() => {
    if (!today?.check_in || today?.check_out) return;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [today?.check_in, today?.check_out]);

  // ---- Derived attendance metrics ----
  // Late-by (in mins) — computed from a fixed 10:00 AM IST cutoff.
  // Expected Punch Out — check_in + 8h30m (IST).
  const punchInDate = today?.check_in ? new Date(today.check_in) : null;
  const punchOutDate = today?.check_out ? new Date(today.check_out) : null;

  const lateByMins = (() => {
    if (!punchInDate) return null;
    // Anchor for "10:00 AM IST on the same working day". Use the calendar date
    // of the punch-in in Asia/Kolkata, then reconstruct as an absolute UTC.
    const istDate = punchInDate.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    // IST is UTC+05:30 → 10:00 IST == 04:30 UTC.
    const cutoff = new Date(`${istDate}T04:30:00Z`);
    const diffMin = Math.round((punchInDate.getTime() - cutoff.getTime()) / 60000);
    return diffMin; // negative => early, positive => late
  })();

  const expectedPunchOut = punchInDate
    ? new Date(punchInDate.getTime() + (8 * 60 + 30) * 60 * 1000)
    : null;

  const liveHoursHms = (() => {
    if (!punchInDate) return null;
    // If already punched out, use the frozen value from the server.
    if (punchOutDate) {
      if (today?.hours_worked_hms) return today.hours_worked_hms;
      const totalSec = Math.max(0, Math.floor((punchOutDate.getTime() - punchInDate.getTime()) / 1000));
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    // Otherwise tick live.
    const now = new Date();
    const totalSec = Math.max(0, Math.floor((now.getTime() - punchInDate.getTime()) / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    // NB: `tick` is referenced solely to defeat memoization by the linter.
    void tick;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  })();

  const doPunch = async () => {
    if (!chosenLoc) { toast.error("Pick your current location"); return; }
    let finalLoc = chosenLoc;
    if (chosenLoc === "Other") {
      const t = otherText.trim();
      if (!t) { toast.error("Please describe where you are"); return; }
      finalLoc = `Other: ${t}`;
    }
    setSubmitting(true);
    try {
      const path = askLoc === "in" ? "/punch/in-with-location" : "/punch/out-with-location";
      await api.post(path, { location: finalLoc });
      const wasPunchOut = askLoc === "out";
      toast.success(wasPunchOut ? "Punched out ✓ Have a great evening!" : "Punched in ✓");
      setAskLoc(null); setChosenLoc(""); setOtherText("");
      await load();
      onChange && onChange();
      // After Punch In, if the user hasn't picked their daily icon yet,
      // prompt them to select one now (v109.9.91).
      if (!wasPunchOut) {
        try {
          const { data: ico } = await api.get("/me/daily-icon");
          if (!ico?.icon) setAskIcon(true);
        } catch { /* ignore */ }
      }
      // After Punch Out, if today's daily report isn't submitted yet, prompt the user.
      if (wasPunchOut) {
        try {
          const { data: rs } = await api.get("/my-daily-report-status");
          setReportStatus(rs);
          if (!rs?.submitted) setAskReport(true);
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePunchIn = () => { setChosenLoc(""); setOtherText(""); setAskLoc("in"); };
  const handlePunchOut = () => { setChosenLoc(""); setOtherText(""); setAskLoc("out"); };

  const hasIn = !!today?.check_in;
  const hasOut = !!today?.check_out;
  const todayStr = fmtDatePR(new Date());

  return (
    <div
      className="pk-depth pk-depth-blue relative overflow-hidden rounded-xl p-5 group h-full"
      data-testid="punch-widget"
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-blue-200/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-indigo-200/20 rounded-full blur-3xl" />
      <div className="relative flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Clock size={22} strokeWidth={2} className="text-blue-600 shrink-0 group-hover:scale-110 transition-transform" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-blue-700">Attendance</div>
              <div className="text-[10.5px] text-blue-600/80 font-semibold">{todayStr}</div>
            </div>
          </div>
          <div className="flex items-start gap-6 mt-3 flex-wrap">
            <div className="min-w-[92px]">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Punch In</div>
              <div className="heading text-2xl font-extrabold text-slate-900 mono" data-testid="punch-in-time">{formatTime(today?.check_in)}</div>
              {/* Late-by pill — only when we have a punch-in */}
              {punchInDate && lateByMins !== null && (
                lateByMins > 0 ? (
                  <div
                    className="mt-1 inline-flex items-center gap-1 rounded-full bg-rose-100 border border-rose-200 px-2 py-0.5 text-[10px] font-bold text-rose-700"
                    data-testid="punch-late-by"
                    title="Counted from 10:00 AM IST"
                  >
                    <AlertCircle size={10} strokeWidth={2.4} />
                    Late by {lateByMins} min{lateByMins === 1 ? "" : "s"}
                  </div>
                ) : (
                  <div
                    className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold text-emerald-700"
                    data-testid="punch-on-time"
                    title="Counted from 10:00 AM IST"
                  >
                    <CheckCircle2 size={10} strokeWidth={2.4} />
                    On time{lateByMins < 0 ? ` · early ${Math.abs(lateByMins)}m` : ""}
                  </div>
                )
              )}
            </div>
            <div className="h-10 w-px bg-blue-200 hidden sm:block" />
            <div className="min-w-[92px]">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Punch Out</div>
              <div className="heading text-2xl font-extrabold text-slate-900 mono" data-testid="punch-out-time">{formatTime(today?.check_out)}</div>
              {/* Approx punch-out (Punch In + 8h30m) — only until user actually punches out */}
              {punchInDate && !punchOutDate && expectedPunchOut && (
                <div
                  className="mt-1 inline-flex items-center gap-1 rounded-full bg-sky-100 border border-sky-200 px-2 py-0.5 text-[10px] font-bold text-sky-700"
                  data-testid="punch-expected-out"
                  title="Punch In + 8h 30m"
                >
                  ~ Expected {expectedPunchOut.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })}
                </div>
              )}
            </div>
            {(liveHoursHms || today?.hours_worked != null) && (
              <>
                <div className="h-10 w-px bg-blue-200 hidden sm:block" />
                <div className="min-w-[112px]">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">
                    Working Hours
                    {!punchOutDate && punchInDate && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" title="Ticking live" />
                    )}
                    {punchOutDate && <span className="text-[9px] text-slate-400 uppercase">Locked</span>}
                  </div>
                  <div
                    className={`heading text-2xl font-extrabold mono ${punchOutDate ? "text-emerald-600" : "text-emerald-700"}`}
                    data-testid="punch-working-hours"
                  >
                    {liveHoursHms || today.hours_worked}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            {!hasIn && (
              <Button
                onClick={handlePunchIn}
                disabled={submitting || loading}
                className="bg-gradient-to-br from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white h-10 shadow-sm"
                data-testid="punch-in-button"
              >
                <LogIn size={15} strokeWidth={1.75} className="mr-1.5" />
                Punch In
              </Button>
            )}
            {hasIn && !hasOut && (
              <Button
                onClick={handlePunchOut}
                disabled={submitting || loading}
                className="bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white h-10 shadow-sm"
                data-testid="punch-out-button"
              >
                <LogOut size={15} strokeWidth={1.75} className="mr-1.5" />
                Punch Out
              </Button>
            )}
            {hasIn && hasOut && (
              <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-[12px] text-emerald-700 font-semibold" data-testid="punch-done">
                ✓ Day completed
              </div>
            )}
            <Button
              variant={reportStatus?.submitted ? "outline" : "default"}
              onClick={() => {
                if (reportStatus?.submitted) {
                  // v109.9.72 — dashboard passes onViewReport to open the
                  // in-place read-only banner. Fallback to the module page
                  // for non-dashboard hosts (My Attendance etc.)
                  if (onViewReport) onViewReport(reportStatus.report || null);
                  else navigate("/m/hr_daily_report");
                } else if (onSubmitReport) {
                  onSubmitReport();
                } else {
                  navigate("/m/hr_daily_report");
                }
              }}
              className={`h-10 shadow-sm ${reportStatus?.submitted ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50 bg-white" : "bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"}`}
              data-testid="submit-report-button"
              title={reportStatus?.submitted ? "View today's report (read-only)" : "Submit today's daily report"}
            >
              <FileText size={15} strokeWidth={1.75} className="mr-1.5" />
              {reportStatus?.submitted ? "View Today's Report" : "Submit Report"}
            </Button>
          </div>
          {reportStatus && (
            <div className="flex items-center gap-3 text-[11px]" data-testid="report-status-row">
              <ReportChip label="Yesterday" submitted={reportStatus.yesterday?.submitted} testid="report-chip-yesterday" />
              <ReportChip label="Today" submitted={reportStatus.submitted} testid="report-chip-today" />
            </div>
          )}
        </div>
      </div>

      {/* Location picker */}
      <Dialog open={!!askLoc} onOpenChange={(o) => { if (!o) { setAskLoc(null); setChosenLoc(""); setOtherText(""); } }}>
        <DialogContent className="max-w-md" data-testid="punch-loc-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <MapPin size={16} /> Where are you right now?
            </DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">We log the location, IP and timestamp (IST) every time you punch in or out.</div>
          </DialogHeader>
          <div className="py-2 grid grid-cols-2 gap-1.5">
            {PUNCH_LOCATIONS.map((loc) => (
              <button
                key={loc}
                onClick={() => setChosenLoc(loc)}
                className={`text-left px-3 py-2.5 rounded-md border-2 transition-all text-[13px] ${chosenLoc === loc ? "bg-[var(--pk-accent)] border-[var(--pk-accent)] text-white font-semibold" : "bg-white border-slate-200 text-slate-700 hover:border-[var(--pk-accent)]"}`}
                data-testid={`punch-loc-${loc.replace(/[^A-Za-z0-9]/g, "_")}`}
              >
                {chosenLoc === loc && <span className="mr-1.5">✓</span>}{loc}
              </button>
            ))}
          </div>
          {chosenLoc === "Other" && (
            <div className="mt-1">
              <Input
                placeholder="Tell us where you are…"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                maxLength={100}
                data-testid="punch-loc-other-text"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAskLoc(null); setChosenLoc(""); setOtherText(""); }}>Cancel</Button>
            <Button onClick={doPunch} disabled={submitting || !chosenLoc} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="punch-loc-confirm">
              {submitting ? "Sending…" : (askLoc === "in" ? "Punch In" : "Punch Out")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Punch-Out: prompt to submit today's daily report */}
      <Dialog open={askReport} onOpenChange={(o) => { if (!o) setAskReport(false); }}>
        <DialogContent className="max-w-md" data-testid="post-punchout-report-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <FileText size={16} className="text-amber-600" /> Submit today&apos;s daily report?
            </DialogTitle>
            <div className="text-[12.5px] text-slate-600 mt-2">
              You&apos;ve punched out for the day. Would you like to submit your <b>Daily Report</b> now?
              You can also do it later from the sidebar.
            </div>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAskReport(false)} data-testid="post-punchout-report-no">
              No, later
            </Button>
            <Button
              onClick={() => {
                setAskReport(false);
                if (onSubmitReport) onSubmitReport();
                else navigate("/m/hr_daily_report");
              }}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="post-punchout-report-yes"
            >
              <FileText size={14} className="mr-1.5" /> Yes, Submit Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Daily icon picker — v109.9.91. Auto-opens right after Punch-In. */}
      <DailyIconPickerDialog
        open={askIcon}
        onOpenChange={setAskIcon}
        userName={user?.name || ""}
      />
    </div>
  );
}

function ReportChip({ label, submitted, testid }) {
  const cls = submitted
    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
    : "bg-rose-50 border-rose-200 text-rose-700";
  const Icon = submitted ? CheckCircle2 : AlertCircle;
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${cls} font-semibold`} data-testid={testid}>
      <Icon size={11} strokeWidth={2.2} />
      <span className="text-[10.5px] uppercase tracking-wider">{label}</span>
      <span className="text-[10.5px]">·</span>
      <span className="text-[10.5px]">{submitted ? "Submitted" : "Pending"}</span>
    </div>
  );
}
